import { useState, useMemo } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useAuth } from '../../../../contexts/AuthContext';
import { useConfig } from '../../../../contexts/ConfigContext';
import { formatPhoneE164, getSmsDeepLink, getWhatsAppDeepLink } from '../../../../lib/messaging';
import { fnErrorMessage } from '../../../../lib/functionError';

type Channel = 'whatsapp' | 'sms' | 'email';

interface Proposal {
  id: string;
  reference_code?: string;
  property_name?: string;
  /** Group proposals carry the portfolio name here instead of property_name. */
  group_name?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  ambassador_name?: string;
  status?: string;
  sent_at?: string | null;
  /** Secret code in the client's link: without it the page shows no contact details (26 Oct 2026). */
  access_key?: string | null;
}

interface ProposalSendModalProps {
  proposal: Proposal;
  supabase: SupabaseClient;
  /**
   * True when the target is a group (portfolio) proposal. Group proposals
   * live in a different table and are sent through pos-group-proposal-manage,
   * which publishes the group and every child property in one go. Without
   * this the single-proposal handler is used and reports "Proposal not found".
   */
  isGroup?: boolean;
  onClose: () => void;
  onSent?: () => void;
}

const keyParam = (accessKey?: string | null) => (accessKey ? `&k=${encodeURIComponent(accessKey)}` : '');

function buildProposalUrl(refCode: string | undefined, countryCode: string, accessKey?: string | null): string {
  return `https://peaceofsleep.vip/p/?ref=${refCode || ''}&c=${(countryCode || 'za').toLowerCase()}${keyParam(accessKey)}`;
}

function buildAgreementUrl(refCode: string | undefined, countryCode: string, accessKey?: string | null): string {
  return `https://peaceofsleep.vip/${(countryCode || 'za').toLowerCase()}/agreement/?ref=${refCode || ''}${keyParam(accessKey)}`;
}

type SendLocale = 'en' | 'da';

/**
 * Outreach message templates per language. The customer reads these, so they
 * follow the country's own language (Danish for Denmark, from country_config
 * .locale). Operator chrome stays English. Brand terms (Peace of Sleep™) and
 * the tier names (Assured / Distinguished / Signature) are kept verbatim.
 */
const SEND_TEMPLATES: Record<SendLocale, {
  intro: (property: string, url: string, sendoff: string) => string;
  emailBody: (property: string, url: string, sendoff: string) => string;
  subject: (property: string) => string;
  sendoff: (name: string) => string;
}> = {
  en: {
    intro: (property, url, sendoff) => `I wanted to introduce Peace of Sleep™, a hospitality certification centred on guest reassurance, sleep hygiene, and overall wellbeing.\n\nWe’ve identified ${property} as a natural fit and prepared a tailored proposal, which you can view via the link here: ${url}\n\nWe’re seeing a natural uptake across similar properties, so rather than follow up, I wanted to ensure you had the opportunity to review this early. So if it resonates, I’d be happy to guide you through next steps, and can share it via email if preferred.${sendoff}`,
    emailBody: (property, url, sendoff) => `I wanted to introduce Peace of Sleep™, the first of its kind hospitality certification reflecting a property’s commitment to guest reassurance, sleep hygiene, and overall wellbeing. We’ve identified ${property} as a natural fit and have prepared the following for your review. This is an initial assessment and may need amending.\n\n${url}\n\nWellness travel is no longer a niche. Guest review culture has also shifted, and a single comment about a concerning sleep environment can sit on a booking platform indefinitely. What properties can claim about their rooms and what they can prove are increasingly two different things. Peace of Sleep™ closes that gap.\n\nAt its core is a trusted, dry and chemical-free process, structured across three tiers, Assured, Distinguished, and Signature, defined simply by service frequency. Compare them using the button at the bottom of the proposal.\n\nThe uptake since its soft introduction in March has been outstanding with the Tintswalo Group recently becoming the first boutique group in the country to hold the certification across all their properties. We wanted to ensure you had the opportunity to consider this early. I would be delighted to guide you through the next steps.${sendoff}`,
    subject: (property) => `Your Peace of Sleep certification proposal${property ? ' — ' + property : ''}`,
    sendoff: (name) => name ? `\n\n${name}` : '',
  },
  da: {
    intro: (property, url, sendoff) => `Jeg vil gerne introducere Peace of Sleep™, en hospitality-certificering med fokus på gæsternes tryghed, søvnhygiejne og generelle velvære.\n\nVi har udpeget ${property} som et oplagt match og udarbejdet et skræddersyet forslag, som du kan se via linket her: ${url}\n\nVi oplever en naturlig interesse blandt lignende ejendomme, så i stedet for at følge op ville jeg sikre, at du fik mulighed for at se det tidligt. Hvis det giver genklang, hjælper jeg dig gerne videre med de næste skridt, og jeg kan også sende det på e-mail, hvis du foretrækker det.${sendoff}`,
    emailBody: (property, url, sendoff) => `Jeg vil gerne introducere Peace of Sleep™, en hospitality-certificering, der afspejler en ejendoms engagement i gæsternes tryghed, søvnhygiejne og generelle velvære.\n\nVi har udpeget ${property} som et oplagt match til denne nye standard og udarbejdet et skræddersyet forslag til dig, som du kan tilgå via linket nedenfor.\n\n${url}\n\nUdviklet som svar på den voksende bevidsthed om sundere sovemiljøer giver Peace of Sleep™ boutique- og premiumejendomme en raffineret måde at vise, at der, hvor gæsterne sover, er taget hånd om med omtanke, ikke kun for komforten, men for ægte ro i sindet.\n\nI kernen ligger en betroet, kemikaliefri tørproces i medicinsk kvalitet, nu løftet til en anerkendt hospitality-standard. Certificeringen er opbygget i tre niveauer; Assured, Distinguished og Signature, defineret ud fra servicehyppighed. Du kan sammenligne forslaget på tværs af hvert niveau ved blot at klikke på knappen 'Sammenlign' nederst.\n\nInteressen blandt lignende premiumejendomme har været enestående, og i stedet for at følge op ville jeg sikre, at du fik mulighed for at overveje det tidligt. Så hvis det giver genklang, hjælper jeg dig med glæde videre med de næste skridt.${sendoff}`,
    subject: (property) => `Dit Peace of Sleep-certificeringsforslag${property ? ': ' + property : ''}`,
    sendoff: (name) => name ? `\n\nVenlig hilsen,\n${name}` : '',
  },
};

type EmailVariant = {
  key: string;
  label: string;
  subject: (property: string) => string;
  body: (property: string, url: string, sendoff: string, name: string) => string;
};

/**
 * Selectable EMAIL templates per language, shown as a picker in the Email
 * section so the sender can test different outreach angles. Index 0 is the
 * default. The proposal link (which already carries the property's own code)
 * and the signoff are injected automatically, so a template can never send the
 * wrong code. House rule: keep this copy free of em / en dashes.
 *
 * Add more variants here as new outreach copy is supplied.
 */
const EMAIL_VARIANTS: Record<SendLocale, [EmailVariant, ...EmailVariant[]]> = {
  en: [
    {
      key: 'detailed',
      label: 'Detailed',
      subject: SEND_TEMPLATES.en.subject,
      body: (property, url, sendoff) => SEND_TEMPLATES.en.emailBody(property, url, sendoff),
    },
    {
      key: 'concise',
      label: 'Concise',
      subject: () => `The 3 words guests now search for before booking (hint: not 'pool view')`,
      body: (property, url, sendoff, name) => `Hi ${name},\n\nGuest review culture has shifted. A single comment about a concerning sleep experience now sits on booking platforms indefinitely, and what properties claim about their rooms versus what they can prove are increasingly two different things.\n\nPeace of Sleep™ closes that gap, and it is moving fast. Since March, uptake has outpaced expectation. The Tintswalo Group recently became the first boutique group in the country certified across all their properties, and several more are in the pipeline.\n\nWe have prepared an initial assessment for ${property}:\n${url}\n\nWe are only taking on a handful of new properties this quarter. If you would like to secure your spot, WhatsApp "POS - ${property}" to 076 022 4476 and I will call you back to walk you through the next steps.${sendoff}`,
    },
    {
      key: 'hygiene-question',
      label: 'Hygiene question',
      subject: (property) => `What is ${property || 'your property'} doing about mattress hygiene?`,
      // Charlie's rewrite, 2026-09-02, used word for word. The only addition is
      // the assessment link: the copy as supplied carried no link at all, and
      // this email exists to deliver the proposal.
      body: (property, url, sendoff, name) => `Hi ${name},\n\nEvery hotel has a housekeeping standard, a linen standard, a turndown standard. But ask any GM or owner what their mattress hygiene standard is, and most go quiet, not because they don’t care, but because it’s simply never been asked or considered.\n\nThat question matters significantly more than it sounds. 1 in 3 guests live with some form of allergy sensitivity, and the mattress is where they spend 50% of their stay. A beautifully presented room says nothing about what’s actually beneath the sheets, and for that guest, it could mean the difference between a five-star stay and a five-star review that quietly mentions waking up… congested.\n\nThis is exactly the gap that Peace of Sleep™ fills, the gap between “we have a hygiene policy” and “we have an allergy-management standard,” which very few properties currently occupy. Most have one. Almost none have the other.\n\nBridging that gap is what makes it a genuine point of difference rather than another wellness amenity claim. Most already gesture at guest wellbeing with a sleep menu or another amenity answering the same guest instinctive question: Will I actually rest here? But very few can point to anything specific, verifiable, or different from the property next door.\n\nThose who can, take an unclaimed part of the guest experience and make it visible, provable, and more importantly their own. They are the ones who end up defining the category rather than reacting to it.\n\nThat’s why we built Peace of Sleep™: the first certification of its kind, underpinned by a fully audited allergy-management process, dry, chemical-free, zero disruption to operations, and zero downtime.\n\nWe introduced this in March this year to a small number of properties first, by invitation rather than by open enrollment, in the same way any meaningful mark of distinction tends to start. A growing number of boutique properties are already certified, which means that guests, press, and eventually other hotels will point to them as the pioneers having seen this coming before it was asked for.\n\nOnce it is being asked for, being first is no longer available. We’d welcome the chance to show you what this looks like for ${property}\n\n${url}\n\nI look forward to hearing from you,${sendoff}`,
    },
    {
      key: 'schools',
      label: 'Schools',
      subject: (property) => `A question about mattress hygiene at ${property || 'your school'}`,
      body: (property, url, sendoff, name) => `Hi ${name},\n\nA quick question before I introduce ourselves properly: what is ${property} currently doing about mattress hygiene, and how confident are you in the answer?\n\nIt's worth having a clear answer to. About 1 in 3 people live with some form of allergy sensitivity, and in a school environment that's a meaningful number of students, every night. A growing number of schools have started addressing this properly and being recognised for it. Most haven't even considered it yet. The schools that move first get to define the standard; the ones that wait get judged against it.\n\nThat's exactly why we developed Peace of Sleep™, the first certification of its kind, giving schools visible, provable reassurance around sleep hygiene and student wellbeing that parents can trust. It's underpinned by CleanMyBed™, our dry and chemical-free allergen-hygiene process, so the claim is real, not just marketing.\n\nWe've prepared an initial assessment for ${property} and I'd be very happy to walk you through it:\n${url}\n\nSince its successful launch, Peace of Sleep™ is now being rolled out to leading schools across the country. We're bringing ${property} into that early group before this becomes something every parent simply expects. I'd welcome the chance to show you where ${property} would sit.${sendoff}`,
    },
  ],
  da: [
    {
      key: 'detailed',
      label: 'Detailed',
      subject: SEND_TEMPLATES.da.subject,
      body: (property, url, sendoff) => SEND_TEMPLATES.da.emailBody(property, url, sendoff),
    },
  ],
};

export default function ProposalSendModal({ proposal, supabase, isGroup = false, onClose, onSent }: ProposalSendModalProps) {
  const { user } = useAuth();
  const { countryCode, countryConfig } = useConfig();
  const phonePrefix = (countryConfig as unknown as Record<string, string>)?.phone_prefix;
  const [markedChannels, setMarkedChannels] = useState<Set<Channel>>(new Set());
  const [copyingChannel, setCopyingChannel] = useState<Channel | null>(null);
  const [savingChannel, setSavingChannel] = useState<Channel | null>(null);
  const [linkCopyState, setLinkCopyState] = useState<'proposal' | 'agreement' | null>(null);
  const [linkWarning, setLinkWarning] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Per-channel overrides — when the user edits the textarea, the entry
  // for that key sticks. Falls back to the template otherwise so changes
  // to senderName / property name etc. still flow through until the user
  // takes manual control.
  type OverrideKey = 'whatsapp' | 'sms' | 'emailSubject' | 'emailBody';
  const [overrides, setOverrides] = useState<Partial<Record<OverrideKey, string>>>({});
  // Which email template variant is selected (index into EMAIL_VARIANTS[lang]).
  const [emailVariantIdx, setEmailVariantIdx] = useState(0);
  // Operator-entered recipient number, used when the proposal has none on file.
  const [manualPhone, setManualPhone] = useState('');

  const url = buildProposalUrl(proposal.reference_code, countryCode, proposal.access_key);
  const agreementUrl = buildAgreementUrl(proposal.reference_code, countryCode, proposal.access_key);
  // Group proposals carry the portfolio name in group_name; single ones in
  // property_name. Either way this drives the customer-facing message copy.
  const propertyName = proposal.property_name || proposal.group_name || '';
  // The signoff is the LOGGED-IN USER (the person actually sending), not the
  // proposal's ambassador. The mailto / wa.me / sms: handoffs open the
  // sender's own client — using a different name there confuses the recipient.
  // First name only: outreach signs off on a first-name basis.
  const senderName = (user?.first_name || '').trim();
  // The outreach message follows the country's own language; the operator UI
  // stays English. Denmark → Danish, every other country → English.
  const sendLang: SendLocale = (((countryConfig?.locale || 'en').split('-')[0] ?? 'en').toLowerCase() === 'da') ? 'da' : 'en';

  const emailVariants = EMAIL_VARIANTS[sendLang];
  const emailVariant = emailVariants[Math.min(emailVariantIdx, emailVariants.length - 1)] ?? emailVariants[0];
  // Some templates greet the contact by first name; fall back gracefully.
  const contactFirst = (proposal.contact_name || '').trim().split(/\s+/)[0] || (sendLang === 'da' ? 'der' : 'there');

  const templates = useMemo(() => {
    const t = SEND_TEMPLATES[sendLang];
    const property = propertyName || (sendLang === 'da' ? 'din ejendom' : 'your property');
    const sendoff = t.sendoff(senderName);

    return {
      whatsapp: t.intro(property, url, sendoff),
      sms: t.intro(property, url, sendoff),
      email: {
        subject: emailVariant.subject(propertyName),
        body: emailVariant.body(property, url, sendoff, contactFirst),
      },
    };
  }, [propertyName, senderName, url, sendLang, emailVariant, contactFirst]);

  // Effective text — user override wins, otherwise the live template.
  const whatsappText = overrides.whatsapp ?? templates.whatsapp;
  const smsText = overrides.sms ?? templates.sms;
  const emailSubjectText = overrides.emailSubject ?? templates.email.subject;
  const emailBodyText = overrides.emailBody ?? templates.email.body;

  // Recipient number: the one on file, otherwise one the operator types in here.
  // No number means no direct chat, so WhatsApp/SMS stay blocked rather than
  // opening WhatsApp with no recipient (the clumsy "pick a chat" step).
  const storedPhone = formatPhoneE164(proposal.contact_phone, phonePrefix);
  const typedPhone = formatPhoneE164(manualPhone, phonePrefix);
  const effectivePhone = storedPhone || typedPhone;

  // Save a typed number back to the proposal so it is there next time. Single
  // proposals only (group proposals live in another table). Best-effort.
  async function persistTypedPhone() {
    if (storedPhone || !typedPhone || isGroup) return;
    try {
      await supabase.functions.invoke('pos-proposal-manage', {
        body: { action: 'update_contact', id: proposal.id, contact_phone: manualPhone.trim() },
      });
    } catch {
      // non-blocking, the message still opens
    }
  }

  function openWhatsApp() {
    if (!effectivePhone) return;
    window.open(getWhatsAppDeepLink(effectivePhone, whatsappText), '_blank');
    void persistTypedPhone();
  }

  function openSms() {
    if (!effectivePhone) return;
    window.location.href = getSmsDeepLink(effectivePhone, smsText);
    void persistTypedPhone();
  }

  function openEmail() {
    const to = encodeURIComponent(proposal.contact_email || '');
    const subject = encodeURIComponent(emailSubjectText);
    const body = encodeURIComponent(emailBodyText);
    window.open(`mailto:${to}?subject=${subject}&body=${body}`, '_self');
  }

  async function copyLink(kind: 'proposal' | 'agreement') {
    setLinkCopyState(kind);
    try {
      await navigator.clipboard.writeText(kind === 'proposal' ? url : agreementUrl);
    } catch {
      // ignore
    }
    setTimeout(() => setLinkCopyState(null), 1500);
    if (kind === 'proposal') {
      // Surface a one-shot warning the first time per session — pasting this URL
      // into a browser will count as a real customer view. Use Preview instead.
      setLinkWarning(true);
      setTimeout(() => setLinkWarning(false), 6000);
    }
  }

  async function copyToClipboard(channel: Channel) {
    setCopyingChannel(channel);
    const text = channel === 'email'
      ? `${emailSubjectText}\n\n${emailBodyText}`
      : channel === 'whatsapp' ? whatsappText : smsText;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore
    }
    setTimeout(() => setCopyingChannel(null), 1500);
  }

  async function markSent(channel: Channel) {
    setSavingChannel(channel);
    setError(null);
    try {
      // Group proposals are published (group + every child property) through
      // the group handler; single proposals through the single one. The group
      // 'send' action is not channel-specific — the channel is recorded only
      // for the single path.
      const { data, error: fnErr } = isGroup
        ? await supabase.functions.invoke('pos-group-proposal-manage', {
            body: { action: 'send', id: proposal.id },
          })
        : await supabase.functions.invoke('pos-proposal-manage', {
            body: { action: 'mark_sent', id: proposal.id, channel },
          });
      if (fnErr) throw fnErr;
      if (data?.error) throw new Error(data.error);

      setMarkedChannels(prev => {
        const next = new Set(prev);
        next.add(channel);
        return next;
      });
      if (onSent) onSent();
    } catch (err) {
      setError(await fnErrorMessage(err, 'Failed to mark as sent'));
    } finally {
      setSavingChannel(null);
    }
  }

  function renderRow(channel: Channel, label: string, openHandler: () => void) {
    const marked = markedChannels.has(channel);
    const saving = savingChannel === channel;
    const bodyKey: OverrideKey = channel === 'email' ? 'emailBody' : channel;
    const bodyValue = channel === 'whatsapp' ? whatsappText : channel === 'sms' ? smsText : emailBodyText;
    const bodyEdited = overrides[bodyKey] !== undefined;
    const subjectEdited = overrides.emailSubject !== undefined;
    const anyEdit = bodyEdited || (channel === 'email' && subjectEdited);
    // WhatsApp and SMS need a recipient number; block them until one exists.
    const needsPhone = channel === 'whatsapp' || channel === 'sms';
    const blockedNoPhone = needsPhone && !effectivePhone;

    return (
      <div className="form-section mb-md">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
          <h3 className="modal-section-heading m-0">{label}</h3>
          <div className="flex gap-sm items-center">
            {anyEdit && (
              <button
                type="button"
                onClick={() => {
                  setOverrides(prev => {
                    const next = { ...prev };
                    delete next[bodyKey];
                    if (channel === 'email') delete next.emailSubject;
                    return next;
                  });
                }}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textDecoration: 'underline' }}
              >
                Reset to default
              </button>
            )}
            {marked && (
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-success, #16a34a)', fontWeight: 600 }}>
                ✓ Marked sent
              </span>
            )}
          </div>
        </div>
        {channel === 'email' && emailVariants.length > 1 && (
          <div className="flex gap-sm items-center flex-wrap mb-sm">
            <span className="text-xs text-light">Template</span>
            {emailVariants.map((v, i) => (
              <button
                key={v.key}
                type="button"
                className={`btn btn-sm ${i === emailVariantIdx ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => {
                  setEmailVariantIdx(i);
                  // Switching template swaps the copy in, so drop any prior
                  // email edits (they belonged to the old template).
                  setOverrides(prev => {
                    const next = { ...prev };
                    delete next.emailSubject;
                    delete next.emailBody;
                    return next;
                  });
                }}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}
        {channel === 'email' && (
          <input
            type="text"
            className="form-input"
            value={emailSubjectText}
            onChange={(e) => setOverrides(prev => ({ ...prev, emailSubject: e.target.value }))}
            placeholder="Subject"
            style={{ marginBottom: '0.5rem', fontSize: 'var(--text-base)' }}
          />
        )}
        <textarea
          className="form-input"
          value={bodyValue}
          onChange={(e) => setOverrides(prev => ({ ...prev, [bodyKey]: e.target.value }))}
          rows={channel === 'email' ? 8 : 5}
          style={{ resize: 'vertical', fontSize: 'var(--text-base)' }}
        />
        <div className="btn-grid btn-grid--3 mt-sm">
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => copyToClipboard(channel)}
          >
            {copyingChannel === channel ? '✓ Copied' : 'Copy message'}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={openHandler}
            disabled={blockedNoPhone}
          >
            Open in {label}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => markSent(channel)}
            disabled={marked || saving || blockedNoPhone}
          >
            {saving ? 'Saving…' : marked ? '✓ Marked sent via ' + label : 'Mark as sent via ' + label}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-md" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Share Proposal</h2>
          <button type="button" className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)', marginBottom: '1rem' }}>
            {proposal.reference_code} {propertyName && <>· {propertyName}</>} {proposal.contact_name && <>· {proposal.contact_name}</>}
          </div>

          {/* Quick links — copy direct URLs without composing a message. A group
              proposal has no agreement link, so the row is one button wide. */}
          <div className={`btn-grid ${isGroup ? 'btn-grid--1' : ''} mb-lg`}>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => copyLink('proposal')}
            >
              {linkCopyState === 'proposal' ? '✓ Proposal link copied' : 'Copy proposal link'}
            </button>
            {!isGroup && (
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => copyLink('agreement')}
              >
                {linkCopyState === 'agreement' ? '✓ Agreement link copied' : 'Copy agreement link'}
              </button>
            )}
          </div>
          {linkWarning && (
            <div className="alert alert-warning mb-lg">
              <strong>This is the live customer URL.</strong> Avoid pasting it into a browser to test —
              that counts as a real customer view. Use the 🔍 Preview button on the proposal row if
              you&apos;re just checking. To send to the customer, use the Mark as sent buttons below.
            </div>
          )}

          {!storedPhone && (
            <div className="form-group">
              <label className="form-label">Customer WhatsApp / mobile number</label>
              <input
                type="tel"
                className="form-input"
                value={manualPhone}
                onChange={(e) => setManualPhone(e.target.value)}
                placeholder="e.g. 071 234 5678"
              />
              <p className="text-xs text-light">
                This proposal has no number on file, so WhatsApp and SMS are off. Add the customer&apos;s
                number to message them directly{!isGroup ? ', it is saved to the proposal' : ''}. Email always works.
              </p>
            </div>
          )}

          {renderRow('whatsapp', 'WhatsApp', openWhatsApp)}
          {renderRow('sms', 'SMS', openSms)}
          {renderRow('email', 'Email', openEmail)}

          {error && (
            <div className="form-error mt-sm">{error}</div>
          )}

          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', marginTop: '0.75rem', lineHeight: 1.5 }}>
            &ldquo;Open&rdquo; launches the channel app pre-filled with the message. &ldquo;Mark as sent&rdquo; records the send in this proposal &mdash; only click after you&rsquo;ve actually sent the message.
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
